import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageSquare, Pin, Lock, Trash2, Send, Sparkles, Plus, Users as UsersIcon } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { BadgeRow } from "../../components/BadgeChip";

function fmtRelative(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  if (ms < 2_592_000_000) return `${Math.floor(ms / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString("en-GB");
}

function TierBadge({ tier, role }) {
  if (role === "admin")
    return <span className="text-[10px] px-1.5 py-0.5 bg-saffron text-navy rounded font-medium uppercase tracking-wider">Admin</span>;
  if (tier === "lifetime")
    return <span className="text-[10px] px-1.5 py-0.5 bg-saffron/15 text-saffron rounded font-medium uppercase tracking-wider">Lifetime</span>;
  if (tier === "annual")
    return <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/15 text-blue-600 rounded font-medium uppercase tracking-wider">Annual</span>;
  return <span className="text-[10px] px-1.5 py-0.5 bg-navy/10 text-navy/60 rounded font-medium uppercase tracking-wider">Member</span>;
}

/** Render the author name as a link to their profile page when we have a
 *  membership_id, otherwise a plain span. */
function AuthorLink({ author, className = "" }) {
  if (!author) return <span className={className}>Member</span>;
  const name = author.name || "Member";
  if (author.membership_id) {
    return (
      <Link
        to={`/platform/profile/${author.membership_id}`}
        className={`hover:text-saffron hover:underline ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {name}
      </Link>
    );
  }
  return <span className={className}>{name}</span>;
}

// ──────────────────────────────────────────────────────────────────────────────
export function CommunityHome() {
  const [categories, setCategories] = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const { user } = useAuth();

  const load = async (cat = activeCat) => {
    try {
      const url = cat === "all" ? "/community/threads" : `/community/threads?category=${cat}`;
      const res = await api.get(url);
      setThreads(res.data.threads || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    api.get("/community/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
    load("all");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(activeCat); /* eslint-disable-next-line */ }, [activeCat]);

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-[1500px] mx-auto" data-testid="community-home">
      <Link to="/platform" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Community</p>
          <h1 className="font-display text-4xl text-navy">Discussions, Wings & Chapters</h1>
          <p className="mt-2 text-navy/65 max-w-2xl">
            Connect with the Sangha across the UK and the world. Read freely; post when logged in.
          </p>
        </div>
        {user && (
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/platform/community/members"
              data-testid="community-members-link"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-navy/15 text-navy text-sm rounded-md hover:border-saffron hover:text-saffron"
            >
              <UsersIcon size={15} /> Member directory
            </Link>
            <button
              onClick={() => setShowNew(true)}
              data-testid="community-new-thread"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-navy text-pearl text-sm rounded-md hover:bg-navy-light"
            >
              <Plus size={16} /> Start a discussion
            </button>
          </div>
        )}
      </div>

      <div className="mt-7 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1 space-y-1.5" data-testid="community-categories">
          <button
            onClick={() => setActiveCat("all")}
            className={`w-full text-left px-3.5 py-2 rounded-md text-sm transition-colors ${activeCat === "all" ? "bg-navy text-pearl" : "text-navy/75 hover:bg-navy/5"}`}
            data-testid="community-cat-all"
          >
            <Sparkles size={13} className="inline mr-2" /> All discussions
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              data-testid={`community-cat-${c.id}`}
              className={`w-full text-left px-3.5 py-2 rounded-md text-sm transition-colors ${activeCat === c.id ? "bg-navy text-pearl" : "text-navy/75 hover:bg-navy/5"}`}
            >
              <div>{c.label}</div>
              <div className={`text-[10px] mt-0.5 ${activeCat === c.id ? "text-pearl/55" : "text-navy/45"}`}>{c.description}</div>
            </button>
          ))}
        </aside>

        <main className="lg:col-span-3">
          {threads.length === 0 ? (
            <div className="bg-white border border-navy/10 rounded-xl p-12 text-center text-navy/60" data-testid="community-empty">
              <MessageSquare size={28} className="mx-auto mb-3 text-saffron" />
              No discussions yet in this section.
              {user && <div className="mt-2 text-sm">Be the first, click "Start a discussion".</div>}
            </div>
          ) : (
            <div className="bg-white border border-navy/10 rounded-xl divide-y divide-navy/5 overflow-hidden" data-testid="community-thread-list">
              {threads.map((t) => (
                <Link
                  key={t.thread_id}
                  to={`/platform/community/${t.thread_id}`}
                  data-testid={`community-thread-${t.thread_id}`}
                  className="flex items-center gap-4 p-4 hover:bg-pearl/40"
                >
                  <div className="w-10 h-10 rounded-full bg-saffron/15 flex items-center justify-center flex-shrink-0 text-saffron font-medium">
                    {(t.author?.name || "M").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.pinned && <Pin size={11} className="text-saffron" />}
                      {t.locked && <Lock size={11} className="text-navy/45" />}
                      <span className="font-display text-base text-navy truncate">{t.title}</span>
                    </div>
                    <div className="text-xs text-navy/55 mt-0.5 flex items-center gap-2 flex-wrap">
                      <AuthorLink author={t.author} className="font-medium text-navy/75" />
                      <TierBadge tier={t.author?.membership_tier} role={t.author?.role} />
                      <span>·</span>
                      <span>{fmtRelative(t.last_activity_at || t.created_at)}</span>
                    </div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <div className="text-sm text-navy/70">{t.reply_count || 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-navy/45">{(t.reply_count || 0) === 1 ? "reply" : "replies"}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>

      {showNew && <NewThreadModal categories={categories} initialCategory={activeCat === "all" ? "general" : activeCat} onClose={() => setShowNew(false)} onCreated={(t) => { setShowNew(false); load(activeCat); }} />}
    </div>
  );
}

function NewThreadModal({ categories, initialCategory, onClose, onCreated }) {
  const [category, setCategory] = useState(initialCategory || "general");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    try {
      const res = await api.post("/community/threads", { category, title: title.trim(), body: body.trim() });
      toast.success("Discussion posted");
      onCreated(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not post.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="community-new-modal">
      <form onSubmit={submit} className="bg-pearl rounded-xl shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-navy/10 font-display text-lg text-navy">Start a discussion</div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Section</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="form-input w-full" data-testid="community-new-category">
              {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={160} className="form-input w-full" data-testid="community-new-title" autoFocus />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">First post</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={6} maxLength={8000} className="form-input w-full font-serif" placeholder="Share your thought, question or news…" data-testid="community-new-body" />
          </label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-navy/10">
          <button type="button" onClick={onClose} className="px-4 py-2 text-navy/60 hover:text-navy text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="px-5 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light disabled:opacity-40" data-testid="community-new-submit">
            {busy ? "Posting…" : "Post"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
export function CommunityThread() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const res = await api.get(`/community/threads/${threadId}`);
      setThread(res.data);
    } catch (err) {
      toast.error("Discussion not found");
      navigate("/platform/community");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threadId]);

  const submitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/community/threads/${threadId}/replies`, { body: reply.trim() });
      setReply("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not post reply.");
    } finally {
      setBusy(false);
    }
  };

  const removeThread = async () => {
    if (!window.confirm("Delete this whole discussion?")) return;
    try {
      await api.delete(`/community/threads/${threadId}`);
      toast.success("Discussion deleted");
      navigate("/platform/community");
    } catch (e) { toast.error("Could not delete"); }
  };
  const removeReply = async (rid) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/community/replies/${rid}`);
      load();
    } catch (e) { toast.error("Could not delete"); }
  };
  const togglePin = async () => {
    await api.post(`/admin/community/threads/${threadId}/pinned`);
    load();
  };
  const toggleLock = async () => {
    await api.post(`/admin/community/threads/${threadId}/locked`);
    load();
  };

  if (!thread) return <div className="p-10 text-center text-navy/60">Loading…</div>;
  const ownThread = user && thread.author?.id && (user.id === thread.author.id || user.user_id === thread.author.id);

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-3xl mx-auto" data-testid="community-thread-view">
      <Link to="/platform/community" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
        <ArrowLeft size={14} /> All discussions
      </Link>
      <article className="bg-white border border-navy/10 rounded-xl p-6 sm:p-8 shadow-sm">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-saffron">
          {thread.pinned && <><Pin size={11} /> Pinned ·</>}
          {thread.locked && <><Lock size={11} /> Locked ·</>}
          <span>{thread.category}</span>
        </div>
        <h1 className="font-display text-3xl text-navy mt-2">{thread.title}</h1>
        <div className="flex items-center gap-2 text-xs text-navy/55 mt-2 flex-wrap">
          <AuthorLink author={thread.author} className="font-medium text-navy/80" />
          <TierBadge tier={thread.author?.membership_tier} role={thread.author?.role} />
          <span>·</span>
          <span>{fmtRelative(thread.created_at)}</span>
          {(ownThread || isAdmin) && (
            <button onClick={removeThread} className="ml-auto text-red-700 hover:text-red-900" title="Delete discussion" data-testid="community-thread-delete">
              <Trash2 size={13} />
            </button>
          )}
          {isAdmin && (
            <>
              <button onClick={togglePin} className="text-saffron hover:text-saffron-light" title="Toggle pin" data-testid="community-thread-pin"><Pin size={13} /></button>
              <button onClick={toggleLock} className="text-navy/60 hover:text-navy" title="Toggle lock" data-testid="community-thread-lock"><Lock size={13} /></button>
            </>
          )}
        </div>
        <div className="mt-5 text-navy/85 whitespace-pre-wrap leading-relaxed font-serif text-[16px]">{thread.body}</div>
      </article>

      <h2 className="font-display text-lg text-navy mt-8 mb-3">{thread.replies?.length || 0} replies</h2>
      <div className="space-y-3">
        {(thread.replies || []).map((r) => {
          const ownReply = user && r.author?.id && (user.id === r.author.id || user.user_id === r.author.id);
          return (
            <div key={r.reply_id} className="bg-white border border-navy/10 rounded-lg p-4" data-testid={`community-reply-${r.reply_id}`}>
              <div className="flex items-center gap-2 text-xs text-navy/55">
                <AuthorLink author={r.author} className="font-medium text-navy" />
                <TierBadge tier={r.author?.membership_tier} role={r.author?.role} />
                <span>· {fmtRelative(r.created_at)}</span>
                {(ownReply || isAdmin) && (
                  <button onClick={() => removeReply(r.reply_id)} className="ml-auto text-red-700 hover:text-red-900" title="Delete reply">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <div className="mt-2 text-navy/85 whitespace-pre-wrap leading-relaxed text-sm">{r.body}</div>
            </div>
          );
        })}
      </div>

      {user ? (
        thread.locked && !isAdmin ? (
          <div className="mt-6 text-sm text-navy/55 italic text-center bg-pearl border border-navy/10 rounded-lg p-4">
            <Lock size={14} className="inline" /> This discussion is locked. New replies are paused.
          </div>
        ) : (
          <form onSubmit={submitReply} className="mt-6 bg-white border border-navy/10 rounded-lg p-4" data-testid="community-reply-form">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={3}
              maxLength={4000}
              placeholder="Add to the discussion…"
              className="form-input w-full font-serif"
              data-testid="community-reply-input"
            />
            <div className="flex justify-end mt-2">
              <button type="submit" disabled={busy || !reply.trim()} className="px-4 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light disabled:opacity-40 inline-flex items-center gap-2" data-testid="community-reply-submit">
                <Send size={14} /> {busy ? "Posting…" : "Reply"}
              </button>
            </div>
          </form>
        )
      ) : (
        <div className="mt-6 text-center text-sm text-navy/65 bg-pearl border border-navy/10 rounded-lg p-5">
          <Link to="/login" className="text-saffron font-medium hover:text-saffron-light">Login</Link> or <Link to="/membership" className="text-saffron font-medium hover:text-saffron-light">join free</Link> to add a reply.
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
export function MemberDirectory() {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    api.get("/community/members").then((r) => setMembers(r.data.members || [])).catch(() => {});
  }, []);
  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-[1300px] mx-auto" data-testid="member-directory">
      <Link to="/platform/community" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
        <ArrowLeft size={14} /> Community
      </Link>
      <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Member directory</p>
      <h1 className="font-display text-4xl text-navy">Our Sangha</h1>
      <p className="mt-2 text-navy/65 max-w-2xl">
        Members who chose to be visible. To opt out of the directory, edit your profile.
      </p>
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {members.map((m, i) => {
          const photo = m.photo_data_url || m.photo_url;
          const card = (
            <div className="bg-white border border-navy/10 rounded-lg p-4 text-center transition-shadow hover:shadow-md h-full">
              <div className="w-16 h-16 mx-auto rounded-full bg-saffron/15 text-saffron text-xl font-display flex items-center justify-center overflow-hidden">
                {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : (m.name || "M").charAt(0).toUpperCase()}
              </div>
              <div className="mt-3 text-sm font-medium text-navy truncate">{m.name || "Member"}</div>
              <div className="text-[10px] text-navy/55 truncate">{[m.city, m.country].filter(Boolean).join(", ")}</div>
              <div className="mt-2 flex items-center justify-center"><TierBadge tier={m.membership_tier} /></div>
              {m.badges?.length > 0 && (
                <div className="mt-1.5 flex justify-center"><BadgeRow badges={m.badges} max={2} /></div>
              )}
            </div>
          );
          return m.membership_id ? (
            <Link
              key={(m.membership_id || "m") + i}
              to={`/platform/profile/${m.membership_id}`}
              data-testid="directory-member"
              className="block"
            >
              {card}
            </Link>
          ) : (
            <div key={(m.membership_id || "m") + i} data-testid="directory-member">{card}</div>
          );
        })}
      </div>
      {members.length === 0 && (
        <div className="mt-12 text-center text-navy/55">The directory will fill as members register.</div>
      )}
    </div>
  );
}
